import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../../../environments/environment';
import { consts } from '../../../../consts';
import { Utils } from '../../../../shared/utils';
import { NotificationService } from '../../../shared/services/notification.service';
import { InvoiceService } from '../../invoice/services/invoice.service';
import { AdvancePaymentService } from '../../../../modules/expense/advancepayment/advancePayment.service';
import { BailService } from '../../../../modules/expense/bail/bail.service';
import { WorkOrderService } from '../../work-order/services/work-order.service';
import {
  DocumentPickerDialogComponent,
  DocumentPickerOption,
  DocumentPickerPage,
} from '../components/document-picker-dialog/document-picker-dialog.component';

export interface AdvancePaymentAssignData {
  uid: string;
  totalAmount: number;
  clientUid: string;
  clientName?: string;
}

export interface SupplierAdvanceAssignData {
  uid: string;
  totalAmount: number;
  providerUid: string;
  providerName?: string;
}

export interface BailAssignData {
  uid: string;
  totalAmount: number;
  providerUid: string;
  providerName?: string;
}

export interface WorkOrderAssignData {
  uid: string;
  orderNumber?: number | string;
}

@Injectable({ providedIn: 'root' })
export class DocumentAssignService {
  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private invoiceService: InvoiceService,
    private advancePaymentService: AdvancePaymentService,
    private bailService: BailService,
    private workOrderService: WorkOrderService,
    private notify: NotificationService,
  ) { }

  assignAdvancePayment(data: AdvancePaymentAssignData): Observable<boolean> {
    const hasClient = !!data.clientUid && data.clientUid !== consts.EMPTY_GUID;
    return DocumentPickerDialogComponent.open(this.dialog, {
      title: 'Asignar anticipo a factura',
      subtitle: data.clientName,
      icon: 'link',
      highlight: { label: 'Importe del anticipo', value: Utils.FormatMoney(data.totalAmount) + ' €' },
      help: 'Elige la factura pendiente de cobro a la que quieres asignar el anticipo.',
      searchPlaceholder: 'Número, fecha o importe',
      confirmText: 'Asignar a',
      savingText: 'Asignando anticipo…',
      errorText: 'No se pudo asignar el anticipo',
      sources: [{
        key: 'invoice',
        label: 'Facturas',
        emptyText: 'Este cliente no tiene facturas pendientes de cobro con importe suficiente para cubrir el anticipo.',
        load: params => this.loadInvoices({
          ...params,
          receiverUid: hasClient ? data.clientUid : null,
          isPaided: false,
          excludeRectifying: true,
          minPending: data.totalAmount || 0,
        }, true),
      }],
      pick: option => this.advancePaymentService.loadData(data.uid).then(advancePayment => {
        advancePayment.invoiceUid = option.uid;
        advancePayment.expenseNumber = option.title;
        return this.advancePaymentService.editData(data.uid, advancePayment);
      }),
    }).pipe(map(result => {
      if (!result) return false;
      this.notify.ok(`Anticipo asignado a la factura ${result.option.title}`);
      return true;
    }));
  }

  assignSupplierAdvance(data: SupplierAdvanceAssignData): Observable<boolean> {
    const hasProvider = !!data.providerUid && data.providerUid !== consts.EMPTY_GUID;
    return DocumentPickerDialogComponent.open(this.dialog, {
      title: 'Asignar anticipo a gasto',
      subtitle: data.providerName,
      icon: 'link',
      highlight: { label: 'Importe del anticipo', value: Utils.FormatMoney(data.totalAmount) + ' €' },
      help: 'Elige el gasto pendiente de pago al que quieres asignar el anticipo.',
      searchPlaceholder: 'Número, proveedor, fecha o importe',
      confirmText: 'Asignar a',
      savingText: 'Asignando anticipo…',
      errorText: 'No se pudo asignar el anticipo',
      sources: [{
        key: 'expense',
        label: 'Gastos',
        emptyText: 'Este proveedor no tiene gastos pendientes de pago con importe suficiente para cubrir el anticipo.',
        load: params => this.loadExpenses({
          ...params,
          receiverUid: hasProvider ? data.providerUid : null,
          isPaided: false,
          excludeRectifying: true,
          minPending: data.totalAmount || 0,
        }),
      }],
      pick: option => this.advancePaymentService.loadData(data.uid).then(advancePayment => {
        advancePayment.invoiceUid = option.uid;
        advancePayment.expenseNumber = option.title;
        return this.advancePaymentService.editData(data.uid, advancePayment);
      }),
    }).pipe(map(result => {
      if (!result) return false;
      this.notify.ok(`Anticipo asignado al gasto ${result.option.title}`);
      return true;
    }));
  }

  assignBail(data: BailAssignData): Observable<boolean> {
    const hasProvider = !!data.providerUid && data.providerUid !== consts.EMPTY_GUID;
    return DocumentPickerDialogComponent.open(this.dialog, {
      title: 'Asignar fianza a gasto',
      subtitle: data.providerName,
      icon: 'link',
      highlight: { label: 'Importe de la fianza', value: Utils.FormatMoney(data.totalAmount) + ' €' },
      help: 'Elige el gasto pendiente de pago al que quieres asignar la fianza.',
      searchPlaceholder: 'Número, proveedor, fecha o importe',
      confirmText: 'Asignar a',
      savingText: 'Asignando fianza…',
      errorText: 'No se pudo asignar la fianza',
      sources: [{
        key: 'expense',
        label: 'Gastos',
        emptyText: 'Este proveedor no tiene gastos pendientes de pago con importe suficiente para cubrir la fianza.',
        load: params => this.loadExpenses({
          ...params,
          receiverUid: hasProvider ? data.providerUid : null,
          isPaided: false,
          excludeRectifying: true,
          minPending: data.totalAmount || 0,
        }),
      }],
      pick: option => this.bailService.loadData(data.uid).then(bail => {
        bail.invoiceUid = option.uid;
        bail.expenseNumber = option.title;
        return this.bailService.editData(data.uid, bail);
      }),
    }).pipe(map(result => {
      if (!result) return false;
      this.notify.ok(`Fianza asignada al gasto ${result.option.title}`);
      return true;
    }));
  }

  assignWorkOrder(data: WorkOrderAssignData): Observable<boolean> {
    return DocumentPickerDialogComponent.open(this.dialog, {
      title: 'Asignar orden de trabajo',
      subtitle: data.orderNumber != null ? `Orden nº ${data.orderNumber}` : undefined,
      icon: 'link',
      help: 'Elige la factura o el presupuesto al que pertenece esta orden de trabajo.',
      searchPlaceholder: 'Número, cliente, fecha o importe',
      confirmText: 'Asignar a',
      savingText: 'Asignando orden de trabajo…',
      errorText: 'No se pudo asignar la orden de trabajo',
      sources: [
        {
          key: 'invoice',
          label: 'Facturas',
          emptyText: 'No hay facturas.',
          load: params => this.loadInvoices(params, false),
        },
        {
          key: 'preinvoice',
          label: 'Presupuestos',
          emptyText: 'No hay presupuestos.',
          load: params => this.loadPreinvoices(params),
        },
      ],
      pick: option => firstValueFrom(this.workOrderService.assign(data.uid, option.uid)).then(res => {
        if (!res || !res.isSuccess) throw new Error();
      }),
    }).pipe(map(result => {
      if (!result) return false;
      const kind = result.sourceKey === 'preinvoice' ? 'al presupuesto' : 'a la factura';
      this.notify.ok(`Orden de trabajo asignada ${kind} ${result.option.title}`);
      return true;
    }));
  }

  private loadInvoices(filter: any, showPending: boolean): Observable<DocumentPickerPage> {
    return this.invoiceService.getSummaryList({ ...filter, sortField: 'InvoiceRelease', sortDirection: 'desc' }).pipe(
      map((res: any) => {
        if (!res || !res.isSuccess) throw new Error();
        return {
          total: res.total || 0,
          items: (res.values || []).map((row: any): DocumentPickerOption => ({
            uid: row.uid,
            title: row.invoiceNumber,
            subtitle: this.subtitle(row.receiverName, row.invoiceRelease),
            amount: row.total,
            hint: showPending ? `Pendiente ${Utils.FormatMoney((row.total || 0) - (row.totalPaided || 0))} €` : undefined,
            raw: row,
          })),
        };
      })
    );
  }

  private loadExpenses(filter: any): Observable<DocumentPickerPage> {
    return this.http.post<any>(`${environment.apiUrl}/api/v2/expenses/list`, {
      ...filter,
      sortField: 'InvoiceRelease',
      sortDirection: 'desc',
    }).pipe(
      map((res: any) => {
        if (!res || !res.isSuccess) throw new Error();
        return {
          total: res.total || 0,
          items: (res.values || []).map((row: any): DocumentPickerOption => ({
            uid: row.uid,
            title: row.invoiceNumber || row.issuerName || 'Sin número',
            subtitle: this.subtitle(row.invoiceNumber ? row.issuerName : '', row.invoiceRelease),
            amount: row.total,
            hint: `Pendiente ${Utils.FormatMoney((row.total || 0) - (row.totalPaided || 0))} €`,
            raw: row,
          })),
        };
      })
    );
  }

  private loadPreinvoices(params: { page: number; pageSize: number; search: string | null }): Observable<DocumentPickerPage> {
    return this.http.post<any>(`${environment.apiUrl}/api/v2/preinvoices/list`, {
      ...params,
      sortField: 'InvoiceRelease',
      sortDirection: 'desc',
    }).pipe(
      map((res: any) => {
        if (!res || !res.isSuccess) throw new Error();
        return {
          total: res.total || 0,
          items: (res.values || []).map((row: any): DocumentPickerOption => ({
            uid: row.uid,
            title: row.preInvoiceNumber,
            subtitle: this.subtitle(row.receiverName, row.invoiceRelease),
            amount: row.total,
            hint: row.invoiceNumber ? `Facturado (${row.invoiceNumber})` : undefined,
            raw: row,
          })),
        };
      })
    );
  }

  private subtitle(receiverName: string, date: string | Date): string {
    const parts = [receiverName, date ? formatDate(date, 'dd/MM/yyyy', 'es-ES') : null].filter(Boolean);
    return parts.join(' · ');
  }
}
